import { useAuth } from '@smartsash/auth';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './auth.callback.module.css';

export interface CallbackSearch {
  readonly code?: string | undefined;
  readonly state?: string | undefined;
  readonly error?: string | undefined;
}

/**
 * Cognito return leg (Q-09). The FE never handles the OAuth code: Cognito calls the BACKEND
 * back, the backend exchanges the code and establishes our session, and this page only has to
 * pick that session up. `validateSearch` is kept because the browser may still arrive with
 * query parameters — they are read for the error case, never exchanged.
 *
 * HS-859 replaced the HS-837 stub, which redirected blindly to `/dashboard`.
 */
export const Route = createFileRoute('/auth/callback')({
  validateSearch: (search: Record<string, unknown>): CallbackSearch => ({
    code: typeof search.code === 'string' ? search.code : undefined,
    state: typeof search.state === 'string' ? search.state : undefined,
    error: typeof search.error === 'string' ? search.error : undefined,
  }),
  component: AuthCallbackPage,
});

function AuthCallbackPage() {
  const { auth, state } = useAuth();
  const { error } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (error) {
      void navigate({ to: '/login' });
      return;
    }
    if (state.status === 'authenticated') {
      void navigate({ to: '/dashboard' });
      return;
    }
    // The root guard already restored once on the way in; this covers the case where the
    // backend finished establishing the session after that probe.
    if (!auth.ready) {
      void auth.restore();
    }
  }, [auth, error, navigate, state.status]);

  const failed = auth.ready && state.status === 'unauthenticated';

  return (
    <main className={styles.callback}>
      {/* The text swaps pending → failed without a navigation, so it needs a live region for
          the change to reach a screen reader at all. */}
      <p role="status">{failed ? t('auth.callback.failed') : t('auth.callback.pending')}</p>
    </main>
  );
}
